import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/login/LoginPage.vue'),
    meta: { layout: 'auth', title: '登录' },
  },
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/pages/dashboard/DashboardPage.vue'),
    meta: { layout: 'app', title: '工作台', requiresAuth: true },
  },
  {
    path: '/chat',
    name: 'chat',
    component: () => import('@/pages/chat/ChatPage.vue'),
    meta: { layout: 'app', title: '会话中心', requiresAuth: true },
  },
  {
    path: '/chat/:sessionId',
    name: 'chat-detail',
    component: () => import('@/pages/chat/ChatPage.vue'),
    meta: { layout: 'app', title: '会话', requiresAuth: true },
  },
  {
    path: '/memories',
    name: 'memories',
    component: () => import('@/pages/memory/MemoryListPage.vue'),
    meta: { layout: 'app', title: '我的记忆', requiresAuth: true },
  },
  {
    path: '/agents',
    name: 'agents',
    component: () => import('@/pages/agent/AgentListPage.vue'),
    meta: { layout: 'app', title: '智能体', requiresAuth: true },
  },
  {
    path: '/embed-apps',
    name: 'embed-apps',
    component: () => import('@/pages/embed/EmbedAppPage.vue'),
    meta: { layout: 'app', title: '嵌入应用', requiresAuth: true },
  },
  {
    path: '/embed-sessions',
    name: 'embed-sessions',
    component: () => import('@/pages/embed/EmbedSessionPage.vue'),
    meta: { layout: 'app', title: '嵌入会话', requiresAuth: true },
  },
  {
    path: '/embed-sessions/:id',
    name: 'embed-session-detail',
    component: () => import('@/pages/embed/EmbedSessionDetailPage.vue'),
    meta: { layout: 'app', title: '嵌入会话详情', requiresAuth: true },
  },
  {
    path: '/embed/chat',
    name: 'embed-chat',
    component: () => import('@/pages/embed/EmbedChatPage.vue'),
    meta: { layout: 'auth', title: '智能体助手' },
  },
  {
    path: '/agents/:id',
    name: 'agent-detail',
    component: () => import('@/pages/agent/AgentDetailPage.vue'),
    meta: { layout: 'app', title: '智能体编辑', requiresAuth: true },
  },
  {
    path: '/skills',
    name: 'skills',
    component: () => import('@/pages/skill/SkillListPage.vue'),
    meta: { layout: 'app', title: '技能目录', requiresAuth: true },
  },
  {
    path: '/skills/:code',
    name: 'skill-detail',
    component: () => import('@/pages/skill/SkillDetailPage.vue'),
    meta: { layout: 'app', title: '技能详情', requiresAuth: true },
  },
  {
    path: '/tools',
    name: 'tools',
    component: () => import('@/pages/tool/ToolListPage.vue'),
    meta: { layout: 'app', title: '工具管理', requiresAuth: true },
  },
  {
    path: '/approvals',
    name: 'approvals',
    component: () => import('@/pages/approval/ApprovalListPage.vue'),
    meta: { layout: 'app', title: '审批流', requiresAuth: true },
  },
  {
    path: '/kb',
    name: 'kb',
    component: () => import('@/pages/kb/KbListPage.vue'),
    meta: { layout: 'app', title: '知识库', requiresAuth: true },
  },
  {
    path: '/kb/:id',
    name: 'kb-detail',
    component: () => import('@/pages/kb/KbDetailPage.vue'),
    meta: { layout: 'app', title: '知识库详情', requiresAuth: true },
  },
  {
    path: '/models',
    name: 'models',
    component: () => import('@/pages/model/ModelGatewayPage.vue'),
    meta: { layout: 'app', title: '模型网关', requiresAuth: true },
  },
  {
    path: '/audit',
    name: 'audit',
    component: () => import('@/pages/audit/AuditListPage.vue'),
    meta: { layout: 'app', title: '审计中心', requiresAuth: true },
  },
  {
    path: '/audit/:id',
    name: 'audit-detail',
    component: () => import('@/pages/audit/AuditDetailPage.vue'),
    meta: { layout: 'app', title: '任务详情', requiresAuth: true },
  },
  {
    path: '/iot/devices',
    name: 'iot-devices',
    component: () => import('@/pages/iot/IotDeviceListPage.vue'),
    meta: { layout: 'app', title: 'IoT 设备', requiresAuth: true },
  },
  {
    path: '/iot/devices/:id',
    name: 'iot-device-detail',
    component: () => import('@/pages/iot/IotDeviceDetailPage.vue'),
    meta: { layout: 'app', title: '设备详情', requiresAuth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { layout: 'auth', title: '未找到' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

router.beforeEach((to) => {
  const appTitle = import.meta.env.VITE_APP_TITLE || 'static'
  document.title = to.meta.title ? `${to.meta.title} · ${appTitle}` : appTitle

  if (to.meta.requiresAuth) {
    const auth = useAuthStore()
    if (!auth.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }
  }
  return true
})
